import { clearPaymentSession } from "@/domain/payment/payment-await-session"
import {
  clearFinalBilling,
  setAwaitingPaymentAttemptId,
  setAwaitingPaymentOrderId,
} from "@/slices/final-billing-slice"
import { clearActiveUnpaidOrder } from "@/slices/checkout-slice"
import { setCheckoutPhase } from "@/slices/checkout-ux-slice"
import { updateProcessState } from "@/slices/process-slice"
import type { AppDispatch } from "@/store/store"
import { useCartStore } from "@/store/cart/cart-store"
import {
  capturePaymentLockDiagSnapshot,
  logPaymentLockDiag,
} from "@/utils/payment-lock-diagnostics"
import {
  capturePaymentSuccessAuditSnapshot,
  logPaymentSuccessAudit,
} from "@/utils/payment-success-audit"

export type ReleaseReason =
  | "terminal_settled"
  | "terminal_failed"
  | "user_cancel_order"
  | "user_create_new_order"
  | "gateway_failure"
  | "payment_start_failed"
  | "auth_logout"

export interface ReleaseCheckoutPaymentSessionInput {
  reason: ReleaseReason
  dispatch: AppDispatch
  clearCart?: boolean
  clearBilling?: boolean
  /** When false, preserves cart/checkout locks (trap marker cleanup only). Default true. */
  releaseLocks?: boolean
  /** Clear checkout.activeUnpaidOrder. Default: true when clearBilling or cancel/logout reasons. */
  clearActiveUnpaid?: boolean
}

/**
 * Single checkout payment-session cleanup authority.
 * Pure side-effect primitive — no classification, navigation, or UI.
 */
export function releaseCheckoutPaymentSession(
  input: ReleaseCheckoutPaymentSessionInput
): void {
  const { dispatch, clearCart = false, clearBilling = false, releaseLocks = true } = input
  const clearActiveUnpaid =
    input.clearActiveUnpaid ??
    (clearBilling ||
      input.reason === "user_cancel_order" ||
      input.reason === "user_create_new_order" ||
      input.reason === "auth_logout" ||
      input.reason === "terminal_settled")

  const before = capturePaymentLockDiagSnapshot()
  const auditBefore = capturePaymentSuccessAuditSnapshot()
  logPaymentLockDiag("release_checkout_payment_session_before", {
    reason: input.reason,
    clearCart,
    clearBilling,
    releaseLocks,
    clearActiveUnpaid,
    before,
  })
  logPaymentSuccessAudit("release_checkout_payment_session_before", {
    reason: input.reason,
    clearCart,
    clearBilling,
    releaseLocks,
    before: auditBefore,
  })

  clearPaymentSession()

  dispatch(setAwaitingPaymentOrderId(null))
  dispatch(setAwaitingPaymentAttemptId(null))
  dispatch(updateProcessState(false))

  if (clearActiveUnpaid) {
    dispatch(clearActiveUnpaidOrder())
  }

  if (releaseLocks) {
    useCartStore.getState().unlockCart()
    useCartStore.getState().setCheckoutLocked(false)
  }

  if (clearBilling) {
    dispatch(clearFinalBilling())
    dispatch(setCheckoutPhase("cart"))
  }

  if (clearCart) {
    useCartStore.getState().clearCart({ force: true })
  }

  logPaymentLockDiag("release_checkout_payment_session_after", {
    reason: input.reason,
    clearCart,
    clearBilling,
    releaseLocks,
    before,
    after: capturePaymentLockDiagSnapshot(),
  })
  logPaymentSuccessAudit("release_checkout_payment_session_after", {
    reason: input.reason,
    clearCart,
    clearBilling,
    releaseLocks,
    before: auditBefore,
    after: capturePaymentSuccessAuditSnapshot(),
  })
}
